import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SectionHeading } from './ui/SectionHeading';
import { Sparkles, Heart, TreeDeciduous, BookOpen, Map, Plus, ChevronRight } from 'lucide-react';
const modules = [{
  num: 'M1',
  icon: Sparkles,
  title: 'Fundamentos y Epistemología',
  subtitle: 'de la Psicología Holística',
  desc: 'La base científica que respalda tu práctica. Integra lo transpersonal a través de la física cuántica, la epigenética conductual y la neurobiología del trauma. Aprende el encuadre ético para introducir lo espiritual sin sugestionar al paciente.',
  tags: ['Física cuántica', 'Epigenética', 'Neurobiología']
}, {
  num: 'M2',
  icon: Heart,
  title: 'El Cuerpo Resonante',
  subtitle: 'Biodecodificación Aplicada',
  desc: 'Aprende a decodificar el mensaje oculto detrás de los síntomas físicos (ansiedad, colon irritable, migrañas, contracturas). Domina el concepto de Bioshock y descubre el origen embrionario de cada órgano para liberar la emoción retenida.',
  tags: ['Bioshock', 'Síntomas físicos', 'Órganos']
}, {
  num: 'M3',
  icon: TreeDeciduous,
  title: 'Las Raíces del Árbol',
  subtitle: 'Constelaciones Familiares Individuales',
  desc: "Aplica la fenomenología sistémica en el consultorio individual usando figuras de madera, plantillas u objetos. Aprende a rastrear lealtades invisibles, identificar la 'parentificación' y utilizar frases sanadoras para disolver traumas heredados.",
  tags: ['Fenomenología', 'Lealtades', 'Parentificación']
}, {
  num: 'M4',
  icon: BookOpen,
  title: 'El Archivo Cuántico',
  subtitle: 'Registros Akáshicos en la Psicoterapia',
  desc: 'El Akasha como acceso al inconsciente profundo. Aprende la sintonización terapéutica para canalizar información relevante del plan del alma de tu paciente. Estructura preguntas de alto impacto terapéutico, evitando predicciones esotéricas.',
  tags: ['Akasha', 'Sintonización', 'Plan del alma']
}, {
  num: 'M5',
  icon: Map,
  title: 'Cartografía del Síntoma',
  subtitle: 'Diagnóstico Cruzado Multidimensional',
  desc: 'El método de triangulación definitivo. Aprende a diagnosticar simultáneamente a través de las 4 capas: Cuerpo (Biodecodificación), Mente (Psicología), Sistema (Constelaciones) y Alma (Registros). Incluye modelos de expedientes clínicos cruzados.',
  tags: ['Triangulación', '4 capas', 'Expedientes']
}];
export const Modules = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [openMobile, setOpenMobile] = useState<number | null>(0);
  const ActiveIcon = modules[activeIndex].icon;
  return <section className="py-16 md:py-24 bg-cream-100 relative border-t border-gold-500/20" data-id="element-155">
      <div className="container mx-auto px-4 md:px-6" data-id="element-156">
        <SectionHeading title="LOS 5 MÓDULOS CENTRALES" subtitle="Tu hoja de ruta para dominar el diagnóstico y la sanación multidimensional" data-id="element-157" />

        {/* ───── MOBILE: Compact Accordion ───── */}
        <div className="md:hidden max-w-md mx-auto space-y-3" data-id="element-158">
          {modules.map((mod, i) => {
          const Icon = mod.icon;
          const isOpen = openMobile === i;
          return <motion.div key={i} initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: i * 0.05
          }} className={`bg-cream rounded-sm border overflow-hidden transition-all ${isOpen ? 'border-gold-500/50 shadow-md' : 'border-gold-500/15'}`} data-id="element-159">
                <button onClick={() => setOpenMobile(isOpen ? null : i)} className="w-full flex items-center gap-3 p-4 text-left" data-id="element-160">
                  {/* Number badge */}
                  <div className={`flex-shrink-0 w-11 h-11 rounded-full flex items-center justify-center font-serif font-bold text-sm transition-colors ${isOpen ? 'bg-navy text-gold-400' : 'bg-gold-500/10 text-navy border border-gold-500/20'}`} data-id="element-161">
                    {mod.num}
                  </div>

                  {/* Title + icon */}
                  <div className="flex-1 min-w-0" data-id="element-162">
                    <div className="flex items-center gap-2 mb-0.5" data-id="element-163">
                      <Icon className="w-3.5 h-3.5 text-gold-600 flex-shrink-0" data-id="element-164" />
                      <p className="text-[10px] uppercase tracking-widest text-gold-600/80 font-semibold truncate" data-id="element-165">
                        {mod.subtitle}
                      </p>
                    </div>
                    <h3 className="font-serif font-bold text-navy text-base leading-tight" data-id="element-166">
                      {mod.title}
                    </h3>
                  </div>

                  {/* Toggle */}
                  <motion.div animate={{
                rotate: isOpen ? 45 : 0
              }} transition={{
                duration: 0.3
              }} className="flex-shrink-0 text-gold-500" data-id="element-167">
                    <Plus className="w-5 h-5" data-id="element-168" />
                  </motion.div>
                </button>

                <AnimatePresence initial={false} data-id="element-169">
                  {isOpen && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} data-id="element-170">
                      <div className="px-4 pb-4 pt-1 border-t border-gold-500/10" data-id="element-171">
                        <p className="text-navy/75 text-sm leading-relaxed mb-3" data-id="element-172">
                          {mod.desc}
                        </p>
                        <div className="flex flex-wrap gap-1.5" data-id="element-173">
                          {mod.tags.map((tag, ti) => <span key={ti} className="text-[10px] px-2 py-0.5 bg-navy/5 text-navy/70 rounded-full border border-navy/10" data-id="element-174">
                              {tag}
                            </span>)}
                        </div>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>;
        })}
        </div>

        {/* ───── DESKTOP: Interactive Tab Selector ───── */}
        <div className="hidden md:grid grid-cols-12 gap-8 max-w-6xl mx-auto" data-id="element-175">
          {/* Left: Module Selector */}
          <div className="col-span-5 space-y-2" data-id="element-176">
            {modules.map((mod, i) => {
            const Icon = mod.icon;
            const isActive = activeIndex === i;
            return <motion.button key={i} onClick={() => setActiveIndex(i)} onMouseEnter={() => setActiveIndex(i)} initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: i * 0.08
            }} className={`w-full text-left p-5 rounded-sm border transition-all flex items-center gap-4 group relative overflow-hidden ${isActive ? 'bg-navy border-gold-500/50 shadow-lg' : 'bg-cream border-gold-500/15 hover:border-gold-500/30'}`} data-id="element-177">
                  {/* Animated indicator */}
                  {isActive && <motion.div layoutId="moduleIndicator" className="absolute left-0 top-0 bottom-0 w-1 bg-gold-500" data-id="element-178" />}

                  <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center font-serif font-bold transition-colors ${isActive ? 'bg-gold-500 text-navy-900' : 'bg-gold-500/10 text-navy border border-gold-500/20'}`} data-id="element-179">
                    {mod.num}
                  </div>

                  <div className="flex-1 min-w-0" data-id="element-180">
                    <div className="flex items-center gap-2 mb-1" data-id="element-181">
                      <Icon className={`w-3.5 h-3.5 ${isActive ? 'text-gold-400' : 'text-gold-600'}`} data-id="element-182" />
                      <p className={`text-[10px] uppercase tracking-widest font-semibold ${isActive ? 'text-gold-400' : 'text-gold-600/80'}`} data-id="element-183">
                        {mod.subtitle}
                      </p>
                    </div>
                    <h3 className={`font-serif font-bold text-lg leading-tight ${isActive ? 'text-cream' : 'text-navy'}`} data-id="element-184">
                      {mod.title}
                    </h3>
                  </div>

                  <ChevronRight className={`w-5 h-5 flex-shrink-0 transition-all ${isActive ? 'text-gold-400 translate-x-1' : 'text-navy/30 group-hover:text-gold-500'}`} data-id="element-185" />
                </motion.button>;
          })}
          </div>

          {/* Right: Active Module Detail */}
          <div className="col-span-7" data-id="element-186">
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="sticky top-28 bg-navy rounded-sm p-10 shadow-xl border border-gold-500/30 relative overflow-hidden min-h-[420px]" data-id="element-187">
              <div className="absolute inset-0 bg-noise opacity-20 pointer-events-none" data-id="element-188" />
              <div className="absolute -top-20 -right-20 w-64 h-64 bg-gold-500/10 rounded-full blur-3xl pointer-events-none" data-id="element-189" />

              <AnimatePresence mode="wait" data-id="element-190">
                <motion.div key={activeIndex} initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -20
              }} transition={{
                duration: 0.35
              }} className="relative z-10" data-id="element-191">
                  <div className="flex items-center gap-3 mb-6" data-id="element-192">
                    <div className="w-14 h-14 rounded-full bg-gold-500/15 border border-gold-500/40 flex items-center justify-center" data-id="element-193">
                      <ActiveIcon className="w-7 h-7 text-gold-400" data-id="element-194" />
                    </div>
                    <div data-id="element-195">
                      <span className="text-gold-500 font-serif text-sm tracking-widest" data-id="element-196">
                        MÓDULO {modules[activeIndex].num.replace('M', '')}
                      </span>
                      <p className="text-cream-200/60 text-xs uppercase tracking-widest" data-id="element-197">
                        {modules[activeIndex].subtitle}
                      </p>
                    </div>
                  </div>

                  <h3 className="font-serif font-bold text-3xl lg:text-4xl text-cream mb-6 leading-tight" data-id="element-198">
                    {modules[activeIndex].title}
                  </h3>

                  <p className="text-cream-200/80 leading-relaxed text-base mb-8" data-id="element-199">
                    {modules[activeIndex].desc}
                  </p>

                  <div className="flex flex-wrap gap-2 pt-6 border-t border-gold-500/20" data-id="element-200">
                    {modules[activeIndex].tags.map((tag, ti) => <span key={ti} className="text-xs px-3 py-1 bg-gold-500/10 text-gold-300 rounded-full border border-gold-500/30" data-id="element-201">
                        {tag}
                      </span>)}
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Progress dots */}
              <div className="absolute bottom-6 left-10 flex gap-1.5 z-10" data-id="element-202">
                {modules.map((_, i) => <button key={i} onClick={() => setActiveIndex(i)} className={`h-1 rounded-full transition-all ${activeIndex === i ? 'w-8 bg-gold-500' : 'w-1.5 bg-gold-500/30 hover:bg-gold-500/50'}`} aria-label={`Ver módulo ${i + 1}`} data-id="element-203" />)}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
};